import React from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import useJumboLayoutSidebar from "@jumbo/hooks/useJumboLayoutSidebar";
import { SIDEBAR_VIEWS } from "@jumbo/utils/constants/layout";
import { useTranslation } from "react-i18next";

const menuBefore = {
  left: 0,
  top: 0,
  content: `''`,
  position: "absolute",
  display: "inline-block",
  width: "4px",
  height: "100%",
  backgroundColor: "transparent",
};

const JumboNavItem = ({ item, isNested, translate }) => {
  const location = useLocation();
  const { sidebarOptions, setSidebarOptions } = useJumboLayoutSidebar();
  const { t } = useTranslation();

  const isMiniAndClosed = React.useMemo(() => {
    return sidebarOptions?.view === SIDEBAR_VIEWS.MINI && !sidebarOptions?.open;
  }, [sidebarOptions.view, sidebarOptions.open]);

  const label = React.useMemo(() => {
    return translate ? t(item.label) : item.label;
  }, [item, translate, t]);

  if (!item) return null;

  const handleClick = () => {
    setSidebarOptions({ open: false });
  };

  const nested = Boolean(isNested);

  return (
    <ListItemButton
      component={RouterLink}
      to={item.uri}
      {...(item.target ? { target: item.target } : {})}
      state={{ fromNavigation: true }}
      onClick={handleClick}
      sx={{
        minHeight: isMiniAndClosed ? 40 : nested ? 34 : 36,
        width: "100%",
        overflow: "hidden",
        borderRadius: isMiniAndClosed ? "50%" : "0 24px 24px 0",
        margin: isMiniAndClosed ? "0 auto" : "0",
        textDecoration: "none",
        color: "inherit",
        ...(isMiniAndClosed
          ? { width: 40, height: 40, justifyContent: "center" }
          : {}),
        ...(!isMiniAndClosed ? { "&::before": menuBefore } : {}),
        "&:hover": {
          color: (theme) => theme.palette.nav.action.hover,
          backgroundColor: (theme) => theme.palette.nav.background.hover,
          ...(!isMiniAndClosed
            ? {
                "&::before": {
                  ...menuBefore,
                  backgroundColor: (theme) => theme.palette.nav.tick.hover,
                },
              }
            : {}),
        },
        ...(location.pathname === item.uri
          ? {
              color: (theme) => theme.palette.nav.action.active,
              backgroundColor: (theme) => theme.palette.nav.background.active,
              ...(!isMiniAndClosed
                ? {
                    "&::before": {
                      ...menuBefore,
                      backgroundColor: (theme) => theme.palette.nav.tick.active,
                    },
                  }
                : {}),
            }
          : {}),
        p: (theme) => (!isMiniAndClosed ? theme.spacing(0.5, 3) : 0),
        ...(nested && !isMiniAndClosed
          ? {
              pl: (theme) => theme.spacing(5),
              "&::after": {
                content: '""',
                position: "absolute",
                left: (theme) => theme.spacing(3.5),
                top: "50%",
                transform: "translateY(-50%)",
                width: 4,
                height: 4,
                borderRadius: "50%",
                backgroundColor: (theme) => theme.palette.text.secondary,
                pointerEvents: "none",
              },
            }
          : {}),
        ...(isMiniAndClosed ? { justifyContent: "center" } : {}),
      }}
    >
      {!isMiniAndClosed && (
        <ListItemText
          primary={label}
          sx={(theme) => ({
            m: 0,
            "& .MuiTypography-root": {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              ...theme.typography.sideNav,
              fontSize: "0.900rem",
              lineHeight: 1.25,
              ...(nested
                ? {
                    fontSize: "0.8125rem",
                  }
                : {}),
            },
          })}
        />
      )}
    </ListItemButton>
  );
};

export default JumboNavItem;
